import LockIcon from "@mui/icons-material/Lock";
import { Box, Card, CardContent, Tooltip, Typography } from "@mui/material";
import type { ReactNode } from "react";
import { usePermissionLockAndMessage } from "../hooks/usePermissionLockAndMessage";
import { UserPermission, useUser } from "../requests/users";
import { borderRadius, shadows } from "../utils/designSystem";

type FeatureCardProps = {
  title: string;
  description: string;
  icon: ReactNode;
  onClick: () => void;
  isLocked?: boolean;
  lockMessage?: string;
  minUserPermission?: UserPermission;
  leagueBlockedOrganizationIds?: number[];
};

export const FeatureCard = ({
  title,
  description,
  icon,
  onClick,
  isLocked = false,
  lockMessage = "",
  minUserPermission = UserPermission.read,
  leagueBlockedOrganizationIds = [],
}: FeatureCardProps) => {
  const user = useUser();
  let { permissionMessage, permissionLock } = usePermissionLockAndMessage(
    lockMessage,
    isLocked,
    minUserPermission
  );

  // TODO: Remove this. It is a temporary hack to prevent League users from seeing poor UX.
  if (
    !permissionLock &&
    user?.organization_id !== undefined &&
    leagueBlockedOrganizationIds.includes(user?.organization_id)
  ) {
    permissionMessage = "This feature is not yet available for Rugby League.";
    permissionLock = true;
  }

  return (
    <Tooltip
      title={permissionLock ? permissionMessage : ""}
      followCursor={true}
      disableHoverListener={!permissionLock}
    >
      <Card
        sx={{
          height: "100%",
          cursor: permissionLock ? "not-allowed" : "pointer",
          transition: "all 0.3s ease",
          "&:hover": {
            transform: permissionLock ? "none" : "translateY(-4px)",
            boxShadow: permissionLock ? "none" : shadows.lg,
          },
          borderRadius: borderRadius.lg,
          overflow: "hidden",
          position: "relative",
          opacity: permissionLock ? 0.7 : 1,
        }}
        onClick={permissionLock ? undefined : onClick}
      >
        {permissionLock && (
          <Box
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              zIndex: 1,
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              borderRadius: borderRadius.round,
              p: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LockIcon sx={{ color: "white", fontSize: "20px" }} />
          </Box>
        )}
        <CardContent
          sx={{
            p: 4,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              mb: 3,
              p: 2,
              borderRadius: borderRadius.round,
              backgroundColor: permissionLock ? "grey.400" : "primary.main",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "80px",
              height: "80px",
            }}
          >
            {icon}
          </Box>
          <Typography variant="h5" component="h2" sx={{ mb: 2, fontWeight: 600 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {description}
          </Typography>
        </CardContent>
      </Card>
    </Tooltip>
  );
};
